import React, { useState, useEffect } from 'react';
import { 
  X, User, MapPin, Car, Clock, Calendar,
  Search, Plus, Check, Briefcase, Dog, Phone,
  Loader2, Send, Home, AlertCircle, Info
} from 'lucide-react';
import { useJsApiLoader } from '@react-google-maps/api';
import { getPassengers } from '../../services/api';
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_API_KEY } from '../../config/googleMaps';
import PlacesAutocompleteInput from './PlacesAutocompleteInput';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const PRIMARY_RED = '#f30001';

// Vehicle Types
const VEHICLE_TYPES = [
  { id: 'sedan', name: 'Sedan', icon: '🚗', desc: 'Standard' },
  { id: 'suv', name: 'SUV', icon: '🚙', desc: 'Më e madhe' },
  { id: 'van', name: 'Van', icon: '🚐', desc: 'Grupore' },
  { id: 'premium', name: 'Premium', icon: '🚘', desc: 'Luksoz' }
];

// Ride Preferences
const RIDE_PREFERENCES = [
  { id: 'wait_time', name: 'Pritja', icon: Clock, options: ['5 min', '10 min', '15 min', '30 min'] },
  { id: 'luggage', name: 'Bagazh', icon: Briefcase, options: ['Jo', '1', '2+', 'I madh'] },
  { id: 'pet', name: 'Kafshë', icon: Dog, options: ['Jo', 'Po'] }
];

// Map Google Places result to CreateOrderModal location shape
const handlePlaceSelect = (place, onChange) => {
  if (!place?.address) {
    onChange(null);
    return;
  }
  onChange({
    address: place.address,
    name: place.name,
    position: {
      lat: place.lat,
      lng: place.lng
    },
    placeId: place.placeId
  });
};

// Passenger Selector (Compact)
const PassengerSelector = ({ value, onChange, randomPhone, onRandomPhoneChange }) => {
  const [search, setSearch] = useState('');
  const [passengers, setPassengers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showList, setShowList] = useState(false);

  useEffect(() => {
    const fetchPassengers = async () => {
      setLoading(true);
      try { const data = await getPassengers(); setPassengers(data || []); } catch (e) {}
      setLoading(false);
    };
    fetchPassengers();
  }, []);

  const filteredPassengers = passengers.filter(p => 
    p.name?.toLowerCase().includes(search.toLowerCase()) || p.phone?.includes(search)
  );

  return (
    <div className="relative">
      <label className="block text-xs font-medium text-gray-600 mb-1.5">Pasagjeri</label>
      
      {value ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between p-2.5 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center text-white text-sm font-bold">
                {value.name?.charAt(0) || 'P'}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-800">{value.name}</p>
                <p className="text-xs text-gray-500">{value.phone || 'Pa numër'}</p>
              </div>
            </div>
            <button onClick={() => { onChange(null); onRandomPhoneChange?.(''); }} className="text-gray-400 hover:text-red-500 p-1">
              <X className="w-4 h-4" />
            </button>
          </div>
          
          {/* Phone input for random passenger */}
          {value.isRandom && (
            <div className="flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
              <Phone className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <input
                type="tel"
                value={randomPhone || ''}
                onChange={(e) => onRandomPhoneChange?.(e.target.value)}
                placeholder="Nr. telefoni (për shoferin)"
                className="flex-1 bg-transparent text-sm border-none focus:outline-none focus:ring-0 placeholder-amber-400"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setShowList(true); }}
              onFocus={() => setShowList(true)}
              placeholder="Kërko pasagjerin..."
              className="w-full pl-10 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          {showList && (
            <div className="absolute z-20 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
              <button
                onClick={() => { onChange({ id: 'random', name: 'Pasagjer i Rastësishëm', phone: '', isRandom: true }); setShowList(false); }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50 border-b border-gray-100"
              >
                <div className="w-7 h-7 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="w-3.5 h-3.5 text-gray-500" />
                </div>
                <span className="text-sm text-gray-600">Pasagjer i Rastësishëm</span>
              </button>

              {loading ? (
                <div className="p-4 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-gray-400" /></div>
              ) : (
                filteredPassengers.slice(0, 4).map(p => (
                  <button
                    key={p.id}
                    onClick={() => { onChange(p); setShowList(false); setSearch(''); }}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 hover:bg-gray-50"
                  >
                    <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-bold">
                      {p.name?.charAt(0) || 'P'}
                    </div>
                    <div className="text-left flex-1">
                      <p className="text-sm font-medium text-gray-700">{p.name}</p>
                      <p className="text-xs text-gray-400">{p.phone}</p>
                    </div>
                    {p.home_address && <Home className="w-3.5 h-3.5 text-gray-300" />}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Vehicle Type Selector (Compact)
const VehicleTypeSelector = ({ value, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-2">Taksia</label>
    <div className="grid grid-cols-4 gap-2">
      {VEHICLE_TYPES.map(v => (
        <button
          key={v.id}
          onClick={() => onChange(v.id)}
          className={`p-2.5 rounded-lg border transition-all text-center ${
            value === v.id ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <span className="text-xl">{v.icon}</span>
          <p className="text-xs font-medium text-gray-700 mt-1">{v.name}</p>
        </button>
      ))}
    </div>
  </div>
);

// Preferences Selector (Compact)
const PreferencesSelector = ({ preferences, onChange }) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-2">Preferenca</label>
    <div className="space-y-2">
      {RIDE_PREFERENCES.map(pref => (
        <div key={pref.id} className="flex items-center gap-2.5">
          <pref.icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-xs text-gray-500 w-14 flex-shrink-0">{pref.name}</span>
          <div className="flex-1 flex gap-1">
            {pref.options.map((opt, i) => (
              <button
                key={i}
                onClick={() => onChange({ ...preferences, [pref.id]: opt })}
                className={`flex-1 py-1.5 px-2 text-xs rounded transition-colors ${
                  preferences[pref.id] === opt
                    ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                style={preferences[pref.id] === opt ? { backgroundColor: PRIMARY_RED } : {}}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Order Summary Modal (Compact)
const OrderSummaryModal = ({ isOpen, onClose, onConfirm, orderData, loading }) => {
  if (!isOpen || !orderData) return null;
  const vehicle = VEHICLE_TYPES.find(v => v.id === orderData.vehicleType);
  const displayPhone = orderData.passenger?.isRandom ? orderData.randomPhone : orderData.passenger?.phone;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl w-full max-w-sm overflow-hidden animate-scale-in">
        <div className="px-4 py-3 flex items-center gap-2" style={{ backgroundColor: PRIMARY_RED }}>
          <AlertCircle className="w-4 h-4 text-white" />
          <span className="text-sm font-bold text-white">Konfirmo Porosinë</span>
        </div>

        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-400" />
            <span className="text-xs text-gray-800">{orderData.passenger?.name || 'Pasagjer i Rastësishëm'}</span>
            {displayPhone && (
              <span className="text-xs text-gray-500 ml-auto flex items-center gap-1">
                <Phone className="w-3 h-3" />
                {displayPhone}
              </span>
            )}
          </div>

          {orderData.driver && (
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-gray-400" />
              <span className="text-xs text-gray-800">{orderData.driver.name}</span>
            </div>
          )}

          <div className="space-y-1.5 py-2 border-y border-gray-100">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              <span className="text-xs text-gray-600">{orderData.pickup?.address || '-'}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
              <span className="text-xs text-gray-600">{orderData.dropoff?.address || '-'}</span>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-500">
            <span className="text-lg">{vehicle?.icon}</span>
            <span>{vehicle?.name}</span>
            {orderData.scheduledTime && (
              <span className="ml-auto flex items-center gap-1 text-indigo-600">
                <Calendar className="w-3 h-3" />
                {new Date(orderData.scheduledTime).toLocaleString('sq-AL', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>

          <div className="flex items-start gap-1.5 p-2 bg-amber-50 rounded text-[10px] text-amber-700">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            <span>{orderData.driver ? 'Porosia caktohet tek shoferi.' : 'Porosia dërgohet tek shoferët.'}</span>
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
          <button onClick={onClose} disabled={loading} className="flex-1 py-2 px-3 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50">
            Anulo
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="flex-1 py-2 px-3 text-xs text-white rounded-lg hover:opacity-90 flex items-center justify-center gap-1"
            style={{ backgroundColor: PRIMARY_RED }}
          >
            {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            Konfirmo
          </button>
        </div>
      </div>
    </div>
  );
};

// Schedule Modal (Compact)
const ScheduleModal = ({ isOpen, onClose, onConfirm }) => {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl w-full max-w-xs p-4 animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-bold text-gray-800">Programo</span>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Data</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-red-500" />
          </div>
          <div>
            <label className="block text-xs text-gray-600 mb-1">Ora</label>
            <input type="time" value={time} onChange={(e) => setTime(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-1 focus:ring-red-500" />
          </div>
        </div>

        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 py-2 text-xs border border-gray-200 rounded-lg text-gray-600">Anulo</button>
          <button onClick={() => { if (date && time) onConfirm({ date, time }); }} disabled={!date || !time}
            className="flex-1 py-2 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: PRIMARY_RED }}>
            Konfirmo
          </button>
        </div>
      </div>
    </div>
  );
};

// Assign Driver Modal (Compact)
const AssignDriverModal = ({ isOpen, onClose, pickup, drivers, onAssign, onAssignAll }) => {
  const [selectedDriver, setSelectedDriver] = useState(null);
  if (!isOpen) return null;

  const onlineDrivers = drivers.filter(d => d.status === 'online').slice(0, 6);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-xl w-full max-w-md max-h-[80vh] overflow-hidden animate-scale-in">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-bold text-gray-800">Cakto Shoferin</span>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        <div className="p-4">
          <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg mb-3">
            <MapPin className="w-3.5 h-3.5 text-emerald-600" />
            <span className="text-xs text-gray-700 truncate">{pickup?.address || 'Pa vendndodhje'}</span>
          </div>

          <div className="space-y-1.5 max-h-[35vh] overflow-y-auto">
            {onlineDrivers.length > 0 ? onlineDrivers.map((driver) => (
              <button
                key={driver.id}
                onClick={() => setSelectedDriver(driver)}
                className={`w-full flex items-center gap-3 p-2.5 rounded-lg border transition-all ${
                  selectedDriver?.id === driver.id ? 'border-red-500 bg-red-50' : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center relative">
                  <span className="text-sm font-bold text-amber-600">{driver.name?.charAt(0)}</span>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"></span>
                </div>
                <div className="flex-1 text-left">
                  <p className="text-xs font-semibold text-gray-800">{driver.name}</p>
                  <p className="text-[10px] text-gray-400">{driver.vehicle?.plate || '-'}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold" style={{ color: PRIMARY_RED }}>{(Math.random() * 3 + 0.5).toFixed(1)} km</p>
                </div>
                {selectedDriver?.id === driver.id && <Check className="w-4 h-4" style={{ color: PRIMARY_RED }} />}
              </button>
            )) : (
              <div className="text-center py-6 text-gray-400">
                <Car className="w-8 h-8 mx-auto mb-1 opacity-30" />
                <p className="text-xs">Nuk ka shoferë</p>
              </div>
            )}
          </div>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex gap-2">
          <button onClick={onAssignAll} className="flex-1 py-2 text-xs border border-gray-200 rounded-lg text-gray-600 flex items-center justify-center gap-1">
            <Send className="w-3 h-3" /> Të gjithëve
          </button>
          <button onClick={() => selectedDriver && onAssign(selectedDriver)} disabled={!selectedDriver}
            className="flex-1 py-2 text-xs text-white rounded-lg disabled:opacity-50 flex items-center justify-center gap-1"
            style={{ backgroundColor: PRIMARY_RED }}>
            <Check className="w-3 h-3" /> Cakto
          </button>
        </div>
      </div>
    </div>
  );
};

// Main Create Order Modal
const CreateOrderModal = ({ isOpen, onClose, drivers, onSuccess }) => {
  const [passenger, setPassenger] = useState(null);
  const [randomPhone, setRandomPhone] = useState('');
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [vehicleType, setVehicleType] = useState('sedan');
  const [preferences, setPreferences] = useState({ wait_time: '5 min', luggage: 'Jo', pet: 'Jo' });
  const [showSchedule, setShowSchedule] = useState(false);
  const [showAssignDriver, setShowAssignDriver] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Load Google Maps Places for destination/business search
  useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  const createOrder = async (orderData) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Determine phone number - use random phone if passenger is random
      const phoneToSend = orderData.passenger?.isRandom 
        ? orderData.randomPhone 
        : (orderData.passenger?.phone || '');
      
      const response = await fetch(`${API_URL}/api/dispatcher/rides`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          passenger_id: orderData.passenger?.id !== 'random' ? orderData.passenger?.id : null,
          passenger_name: orderData.passenger?.name || 'Pasagjer i Rastësishëm',
          passenger_phone: phoneToSend,
          pickup: orderData.pickup,
          dropoff: orderData.dropoff,
          vehicle_type: orderData.vehicleType,
          preferences: orderData.preferences,
          scheduled_time: orderData.scheduledTime || null,
          driver_id: orderData.driver?.id || null,
          driver_name: orderData.driver?.name || null,
          source: 'dispatcher'
        })
      });
      
      if (response.ok) {
        setShowSummary(false);
        onSuccess?.();
        resetForm();
      }
    } catch (e) { console.error('Error:', e); }
    setLoading(false);
  };

  const handleScheduleConfirm = (schedule) => {
    setShowSchedule(false);
    setSummaryData({ passenger, pickup, dropoff, vehicleType, preferences, scheduledTime: `${schedule.date}T${schedule.time}:00`, driver: null, randomPhone });
    setShowSummary(true);
  };

  const handleAssignDriver = (driver) => {
    setShowAssignDriver(false);
    setSummaryData({ passenger, pickup, dropoff, vehicleType, preferences, scheduledTime: null, driver, randomPhone });
    setShowSummary(true);
  };

  const handleAssignAll = () => {
    setShowAssignDriver(false);
    setSummaryData({ passenger, pickup, dropoff, vehicleType, preferences, scheduledTime: null, driver: null, randomPhone });
    setShowSummary(true);
  };

  const resetForm = () => {
    setPassenger(null); setPickup(null); setDropoff(null);
    setVehicleType('sedan'); setPreferences({ wait_time: '5 min', luggage: 'Jo', pet: 'Jo' });
    setSummaryData(null); setRandomPhone('');
  };

  const canProceed = passenger && pickup;

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
        <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] overflow-hidden animate-scale-in shadow-2xl">
          {/* Header */}
          <div className="px-5 py-3.5 flex items-center justify-between" style={{ backgroundColor: PRIMARY_RED }}>
            <div className="flex items-center gap-2.5">
              <Plus className="w-5 h-5 text-white" />
              <span className="text-base font-bold text-white">Udhëtim i Ri</span>
            </div>
            <button onClick={onClose} className="p-1.5 hover:bg-white/20 rounded">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4 overflow-y-auto max-h-[55vh]">
            <PassengerSelector value={passenger} onChange={setPassenger} randomPhone={randomPhone} onRandomPhoneChange={setRandomPhone} />

            <div className="space-y-2">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Pikë Nisje</label>
                <PlacesAutocompleteInput
                  value={pickup?.address || ''}
                  onChange={(place) => handlePlaceSelect(place, setPickup)}
                  placeholder="Kërko adresën ose biznesin..."
                  type="pickup"
                  country="al"
                />
              </div>
              
              {/* Quick Home Address Button */}
              {passenger?.home_address && (
                <button 
                  type="button"
                  onClick={() => setPickup({ 
                    address: passenger.home_address.address || passenger.home_address, 
                    position: { 
                      lat: passenger.home_address.lat || 41.3275, 
                      lng: passenger.home_address.lng || 19.8187 
                    } 
                  })}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-medium transition ml-1"
                >
                  <Home className="w-3 h-3" /> 
                  Nga Shtëpia: {passenger.home_address.address || passenger.home_address}
                </button>
              )}

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Destinacioni</label>
                <PlacesAutocompleteInput
                  value={dropoff?.address || ''}
                  onChange={(place) => handlePlaceSelect(place, setDropoff)}
                  placeholder="Kërko destinacion me Google Maps..."
                  type="dropoff"
                  country="al"
                />
              </div>
            </div>

            <VehicleTypeSelector value={vehicleType} onChange={setVehicleType} />
            <PreferencesSelector preferences={preferences} onChange={setPreferences} />
          </div>

          {/* Actions */}
          <div className="px-5 py-4 border-t border-gray-100 flex gap-3">
            <button onClick={() => setShowSchedule(true)} disabled={!canProceed || loading}
              className="flex-1 py-2.5 px-4 text-sm border-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ borderColor: PRIMARY_RED, color: PRIMARY_RED }}>
              <Calendar className="w-4 h-4" /> Rezervo
            </button>
            <button onClick={() => setShowAssignDriver(true)} disabled={!canProceed || loading}
              className="flex-1 py-2.5 px-4 text-sm text-white rounded-lg font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50"
              style={{ backgroundColor: PRIMARY_RED }}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Tani
            </button>
          </div>
        </div>
      </div>

      <ScheduleModal isOpen={showSchedule} onClose={() => setShowSchedule(false)} onConfirm={handleScheduleConfirm} />
      <AssignDriverModal isOpen={showAssignDriver} onClose={() => setShowAssignDriver(false)} pickup={pickup} drivers={drivers || []} onAssign={handleAssignDriver} onAssignAll={handleAssignAll} />
      <OrderSummaryModal isOpen={showSummary} onClose={() => setShowSummary(false)} onConfirm={() => createOrder(summaryData)} orderData={summaryData} loading={loading} />

      <style>{`
        @keyframes scaleIn { from { transform: scale(0.95); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .animate-scale-in { animation: scaleIn 0.15s ease-out forwards; }
      `}</style>
    </>
  );
};

export default CreateOrderModal;
